import { useState } from 'react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Plus } from 'lucide-react';

interface AddMilestoneDialogProps {
  onAdd: (milestone: {
    name: string;
    percentage: number;
    description: string;
  }) => void;
  totalContractValue: number;
}
export const cleanMoney = (val:any) => {
  if (val === null || val === undefined) return 0;

  return Number(
    String(val)
      .replace(/,/g, '')     // commas hatao
      .trim()
  );
};


export function AddMilestoneDialog({ onAdd, totalContractValue }: AddMilestoneDialogProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [percentage, setPercentage] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = () => {
    if (name && percentage) {
      onAdd({
        name,
        percentage: parseFloat(percentage),
        description,
      });
      setName('');
      setPercentage('');
      setDescription('');
      setOpen(false);
    }
  };

  const baseAmount = cleanMoney(totalContractValue)
  
  const calculatedAmount =
    baseAmount * (parseFloat(percentage) || 0) / 100;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="lg" className="w-full sm:w-auto">
          <Plus className="mr-2 h-5 w-5" />
          Add Milestone
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add New Milestone</DialogTitle>
          <DialogDescription>
            Create a new milestone for your contract. Total contract value: ${Number(totalContractValue.toString().replace(/[^\d]/g, "") ).toLocaleString()}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="milestone-name">Milestone Name *</Label>
            <Input
              id="milestone-name"
              placeholder="e.g., Foundation Complete"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="percentage">Percentage of Contract *</Label>
            <div className="relative mt-1">
              <Input
                id="percentage"
                type="number"
                min="0"
                max="100"
                step="0.1"
                placeholder="0"
                value={percentage}
                onChange={(e) => setPercentage(e.target.value)}
                className="pr-8"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
            </div>
            {percentage && (
              <p className="text-sm text-green-600 mt-1">
                Amount: ${calculatedAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea
              id="description"
              placeholder="Describe the work included in this milestone..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!name || !percentage}>
            Add Milestone
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
